import { useState, useEffect } from 'react';
import { Plus, ChevronDown, ChevronRight, X } from 'lucide-react';
import { Card, CardContent, CardHeader } from 'src/@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import { Input } from 'src/@/components/ui/input';
import { Button } from 'src/@/components/ui/button';
import { Badge } from 'src/@/components/ui/badge';
import { Checkbox } from 'src/@/components/ui/checkbox';
import { Label } from 'src/@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/@/components/ui/dialog";
import Iconify from 'src/components/Iconify';
import ModalConfimacao from 'src/components/modal/ModalConfimacao';
import ModalCustomField from './ModalCustomField';

import v2_actionListarCustomTabs from 'src/actions/v2/crm/v2_actionListarCustomTabs';
import v2_actionCriarCustomTab from 'src/actions/v2/crm/v2_actionCriarCustomTab';
import v2_actionEditarCustomTab from 'src/actions/v2/crm/v2_actionEditarCustomTab';
import v2_actionDeletarCustomTab from 'src/actions/v2/crm/v2_actionDeletarCustomTab';
import v2_actionCriarCustomField from 'src/actions/v2/crm/v2_actionCriarCustomField';
import v2_actionEditarCustomField from 'src/actions/v2/crm/v2_actionEditarCustomField';
import v2_actionDeletarCustomField from 'src/actions/v2/crm/v2_actionDeletarCustomField';

const TIPO_LABELS = {
  texto: 'Texto',
  selecao: 'Seleção',
  data: 'Data',
  upload: 'Upload'
};

const TIPO_COLORS = {
  texto: 'text-gray-600 border-gray-200 bg-gray-50',
  selecao: 'text-blue-600 border-blue-200 bg-blue-50',
  data: 'text-orange-600 border-orange-200 bg-orange-50',
  upload: 'text-violet-600 border-violet-200 bg-violet-50'
};

export default function TabCustomFields({ cliente, pipelines }) {
  const [selectedPipeline, setSelectedPipeline] = useState('');
  const [tabs, setTabs] = useState([]);
  const [expandedTabs, setExpandedTabs] = useState({});

  // Tab modal state
  const [openTabModal, setOpenTabModal] = useState(false);
  const [actionTab, setActionTab] = useState(null);
  const [tabModalId, setTabModalId] = useState(null);
  const [tabModalNome, setTabModalNome] = useState('');
  const [tabModalPipelineIds, setTabModalPipelineIds] = useState([]);

  // Field modal state
  const [openFieldModal, setOpenFieldModal] = useState(false);
  const [actionField, setActionField] = useState(null);
  const [currentTabId, setCurrentTabId] = useState(null);
  const [fieldId, setFieldId] = useState(null);
  const [fieldNome, setFieldNome] = useState('');
  const [fieldTipo, setFieldTipo] = useState('texto');
  const [fieldOpcoes, setFieldOpcoes] = useState([]);
  const [fieldObrigatorio, setFieldObrigatorio] = useState(false);

  // Delete confirmation
  const [openConfirmDeleteTab, setOpenConfirmDeleteTab] = useState(false);
  const [deleteTabId, setDeleteTabId] = useState(null);
  const [openConfirmDeleteField, setOpenConfirmDeleteField] = useState(false);
  const [deleteFieldId, setDeleteFieldId] = useState(null);

  useEffect(() => {
    if (selectedPipeline) {
      buscarTabs();
    }
  }, [selectedPipeline]);

  async function buscarTabs() {
    try {
      const result = await v2_actionListarCustomTabs(cliente._id, selectedPipeline);
      if (result.status === 200) {
        setTabs(result.data?.data || []);
      }
    } catch (error) {
      console.log(error);
    }
  }

  function getPipelineNome(id) {
    const p = pipelines?.find(p => p._id === id || p._id === String(id));
    return p?.pipeline_nome || p?.nome || id;
  }

  function togglePipelineSelection(pipelineId) {
    setTabModalPipelineIds(prev =>
      prev.includes(pipelineId)
        ? prev.filter(id => id !== pipelineId)
        : [...prev, pipelineId]
    );
  }

  // Tab CRUD
  function openCriarTab() {
    setActionTab('Criar');
    setTabModalId(null);
    setTabModalNome('');
    setTabModalPipelineIds(selectedPipeline ? [selectedPipeline] : []);
    setOpenTabModal(true);
  }

  function openEditarTab(tab) {
    setActionTab('Editar');
    setTabModalId(tab._id);
    setTabModalNome(tab.nome);
    setTabModalPipelineIds((tab.pipeline_ids || []).map(id => String(id)));
    setOpenTabModal(true);
  }

  async function handleSubmitTab() {
    if (!tabModalNome.trim() || tabModalPipelineIds.length === 0) return;
    try {
      if (actionTab === 'Criar') {
        await v2_actionCriarCustomTab(cliente._id, {
          nome: tabModalNome.trim(),
          pipeline_ids: tabModalPipelineIds
        });
      } else {
        await v2_actionEditarCustomTab(tabModalId, {
          scope: {
            nome: tabModalNome.trim(),
            pipeline_ids: tabModalPipelineIds
          }
        });
      }
      setOpenTabModal(false);
      buscarTabs();
    } catch (error) {
      console.log(error);
    }
  }

  async function handleDeletarTab() {
    try {
      await v2_actionDeletarCustomTab(deleteTabId);
      setOpenConfirmDeleteTab(false);
      setDeleteTabId(null);
      buscarTabs();
    } catch (error) {
      setOpenConfirmDeleteTab(false);
      console.log(error);
    }
  }

  // Field CRUD
  function resetFieldForm() {
    setActionField(null);
    setCurrentTabId(null);
    setFieldId(null);
    setFieldNome('');
    setFieldTipo('texto');
    setFieldOpcoes([]);
    setFieldObrigatorio(false);
  }

  async function handleCreateField() {
    try {
      await v2_actionCriarCustomField(cliente._id, {
        nome: fieldNome,
        tipo: fieldTipo,
        opcoes: fieldTipo === 'selecao' ? fieldOpcoes : [],
        obrigatorio: fieldObrigatorio,
        tab_id: currentTabId
      });
      setOpenFieldModal(false);
      resetFieldForm();
      buscarTabs();
    } catch (error) {
      console.log(error);
    }
  }

  async function handleEditField() {
    try {
      await v2_actionEditarCustomField(fieldId, {
        scope: {
          nome: fieldNome,
          tipo: fieldTipo,
          opcoes: fieldTipo === 'selecao' ? fieldOpcoes : [],
          obrigatorio: fieldObrigatorio
        }
      });
      setOpenFieldModal(false);
      resetFieldForm();
      buscarTabs();
    } catch (error) {
      console.log(error);
    }
  }

  async function handleDeleteField() {
    try {
      await v2_actionDeletarCustomField(deleteFieldId);
      setOpenConfirmDeleteField(false);
      setDeleteFieldId(null);
      buscarTabs();
    } catch (error) {
      setOpenConfirmDeleteField(false);
      console.log(error);
    }
  }

  function toggleTab(tabId) {
    setExpandedTabs(prev => ({ ...prev, [tabId]: !prev[tabId] }));
  }

  return (
    <>
      <ModalConfimacao
        open={openConfirmDeleteTab}
        onClose={() => setOpenConfirmDeleteTab(false)}
        onConfirm={handleDeletarTab}
        mensagem="Deseja mesmo excluir esta aba? Todos os campos serão desativados."
      />

      <ModalConfimacao
        open={openConfirmDeleteField}
        onClose={() => setOpenConfirmDeleteField(false)}
        onConfirm={handleDeleteField}
        mensagem="Deseja mesmo excluir este campo?"
      />

      <ModalCustomField
        open={openFieldModal}
        action={actionField}
        nome={fieldNome}
        changeNome={(e) => setFieldNome(e.target.value)}
        tipo={fieldTipo}
        changeTipo={(value) => setFieldTipo(value)}
        opcoes={fieldOpcoes}
        changeOpcoes={(value) => setFieldOpcoes(value)}
        obrigatorio={fieldObrigatorio}
        changeObrigatorio={(checked) => setFieldObrigatorio(checked)}
        onSubmit={actionField === "Criar" ? handleCreateField : handleEditField}
        onClose={() => {
          setOpenFieldModal(false);
          resetFieldForm();
        }}
      />

      {/* Modal Criar/Editar Aba */}
      <Dialog open={openTabModal} onOpenChange={() => setOpenTabModal(false)}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {actionTab === "Criar" ? "Criar aba personalizada" : "Editar aba personalizada"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-gray-700">Nome da aba</Label>
              <Input
                value={tabModalNome}
                onChange={(e) => setTabModalNome(e.target.value)}
                placeholder="Ex: Renovação, Sinistros"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Pipelines vinculados</Label>
              <p className="text-xs text-gray-400">Selecione em quais funis essa aba será exibida</p>
              <div className="space-y-1.5 max-h-[200px] overflow-auto border border-gray-200 rounded-lg p-2">
                {pipelines?.map(p => (
                  <label
                    key={p._id}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-gray-50 cursor-pointer"
                  >
                    <Checkbox
                      checked={tabModalPipelineIds.includes(p._id)}
                      onCheckedChange={() => togglePipelineSelection(p._id)}
                    />
                    <span className="text-sm">{p.pipeline_nome || p.nome}</span>
                  </label>
                ))}
              </div>
              {tabModalPipelineIds.length === 0 && (
                <p className="text-xs text-red-500">Selecione pelo menos um pipeline</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleSubmitTab} disabled={!tabModalNome.trim() || tabModalPipelineIds.length === 0}>
              {actionTab === "Criar" ? "Criar" : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="w-3/4">
        <div className="mb-4 mt-4 flex items-center gap-3">
          <Select value={selectedPipeline} onValueChange={setSelectedPipeline}>
            <SelectTrigger className="w-[300px]">
              <SelectValue placeholder="Selecione um pipeline para visualizar" />
            </SelectTrigger>
            <SelectContent>
              {pipelines?.map(p => (
                <SelectItem key={p._id} value={p._id}>{p.pipeline_nome || p.nome}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedPipeline && (
            <button
              onClick={openCriarTab}
              className="flex items-center gap-2 px-3 py-1.5 bg-gray-900 hover:bg-gray-800 text-white text-xs font-medium rounded-lg transition-colors"
            >
              <Plus size={14} />
              Nova aba
            </button>
          )}
        </div>

        {selectedPipeline && (
          <>
            <div className="space-y-3">
              {tabs.map(tab => (
                <Card key={tab._id} className="border border-gray-100 shadow-sm">
                  <CardHeader className="py-3 px-4 border-b border-dashed border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <button onClick={() => toggleTab(tab._id)} className="text-gray-400 hover:text-gray-600 shrink-0">
                          {expandedTabs[tab._id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </button>
                        <span className="text-sm font-semibold text-gray-800">{tab.nome}</span>
                        <Badge variant="outline" className="text-xs text-gray-500 border-gray-200 shrink-0">
                          {tab.fields?.length || 0} campos
                        </Badge>
                        <div className="flex gap-1 flex-wrap">
                          {(tab.pipeline_ids || []).map(pid => (
                            <Badge key={pid} variant="outline" className="text-[10px] text-violet-600 border-violet-200 bg-violet-50">
                              {getPipelineNome(pid)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => openEditarTab(tab)}
                        >
                          <Iconify icon="ph:pencil-line" width={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => {
                            setDeleteTabId(tab._id);
                            setOpenConfirmDeleteTab(true);
                          }}
                        >
                          <Iconify icon="material-symbols-light:delete-outline" width={14} className="text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  {expandedTabs[tab._id] && (
                    <CardContent className="p-0">
                      {tab.fields?.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Campo</TableHead>
                              <TableHead>Tipo</TableHead>
                              <TableHead>Obrigatório</TableHead>
                              <TableHead className="w-[100px]"></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {tab.fields.map((field, index) => (
                              <TableRow key={index}>
                                <TableCell className="font-medium">{field.nome}</TableCell>
                                <TableCell>
                                  <Badge variant="outline" className={`text-xs font-medium ${TIPO_COLORS[field.tipo]}`}>
                                    {TIPO_LABELS[field.tipo]}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  {field.obrigatorio ? (
                                    <Badge variant="outline" className="text-xs font-medium bg-green-50 text-green-700 border-green-200">Sim</Badge>
                                  ) : (
                                    <span className="text-xs text-gray-400">Não</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <div className="flex gap-2">
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      onClick={() => {
                                        setOpenFieldModal(true);
                                        setActionField('Editar');
                                        setCurrentTabId(tab._id);
                                        setFieldId(field._id);
                                        setFieldNome(field.nome);
                                        setFieldTipo(field.tipo);
                                        setFieldOpcoes(field.opcoes || []);
                                        setFieldObrigatorio(field.obrigatorio);
                                      }}
                                    >
                                      <Iconify icon="ph:pencil-line" />
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      onClick={() => {
                                        setDeleteFieldId(field._id);
                                        setOpenConfirmDeleteField(true);
                                      }}
                                    >
                                      <Iconify color="red" icon="material-symbols-light:delete-outline" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-8 text-center">
                          <p className="text-sm text-gray-400">Nenhum campo configurado nesta aba</p>
                        </div>
                      )}
                      <div className="px-4 py-3 border-t border-dashed border-gray-200">
                        <button
                          className="flex items-center gap-1.5 text-xs font-medium text-[#7C3AED] hover:text-[#6d28d9] transition-colors"
                          onClick={() => {
                            setOpenFieldModal(true);
                            setActionField('Criar');
                            setCurrentTabId(tab._id);
                          }}
                        >
                          <Plus size={14} />
                          Adicionar campo
                        </button>
                      </div>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>

            {tabs.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-center mt-4">
                <p className="text-sm text-gray-400">Nenhuma aba personalizada cadastrada para este pipeline</p>
              </div>
            )}
          </>
        )}

        {!selectedPipeline && (
          <div className="flex flex-col items-center justify-center py-12 text-center mt-4">
            <p className="text-sm text-gray-400">Selecione um pipeline para configurar campos personalizados</p>
          </div>
        )}
      </div>
    </>
  );
}
